import { useState } from 'react';
import { TcountryCode } from 'components/NudosComponents/BordFlag/BordFlag.types';
import { IProductList, ORDER_STATUS_NAME_TRIM, SLA_STATUS } from 'types/orders';
import { formatShortDate } from 'utils/formatDatesAndHours';
import { formatRoundedUpNumber } from 'utils/formatNumbers';
import useUsersState from 'state/useUsersState';
import { bordColorTheme, nudosBlue } from 'styles/styles.constants';

const MAX_VISIBLE_SHIPMENTS = 3;
const MAX_EXPANDABLE_SHIPMENTS = 5;

/**
 * Hook personalizado que encapsula toda la lógica de estados y handlers
 * para el componente OperationsCard.
 * @param data - Datos de la orden (IProductList)
 * @returns Objeto con estados, handlers y valores derivados para el componente
 */
const useOperationsCard = (data?: IProductList) => {
  const { users } = useUsersState();

  const [isFocused, setIsFocused] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [isShipmentsExpanded, setIsShipmentsExpanded] = useState(false);
  const [isShipmentDrawerOpen, setIsShipmentDrawerOpen] = useState(false);
  const [isPreparationDrawerOpen, setIsPreparationDrawerOpen] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null);
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0)

  // Valores extraídos de data con valores por defecto
  const orderNumber = data?.orderId?.toString();
  const companyName = data?.organizationName || 'Sin nombre';
  const countryFlag = data?.countryOrganization?.code?.toLowerCase() as TcountryCode;
  const countryName = data?.countryOrganization?.name || '';
  const totalShipmentsNumber = data?.numberOfShipments || 0;
  const formattedCancelingDate = data?.cancelledDate ? formatShortDate(data.cancelledDate)?.onlyDate : 'Sin fecha';

  const slaDate = data?.slaDeadline ? formatShortDate(data.slaDeadline)?.onlyDate : 'Sin fecha';
  const allShipmentsData = data?.shipments || [];
  const shipmentsCount = allShipmentsData.length;

  /**
   * Determina si todos los envíos tienen el mismo status.
   * Usado para ocultar el botón de focus cuando no hay diferencias de estado entre envíos.
   */
  const allShipmentsHaveSameStatus = (() => {
    if (allShipmentsData.length <= 1) return true;
    const firstStatusId = allShipmentsData[0]?.statusId;
    return allShipmentsData.every(shipment => shipment.statusId === firstStatusId);
  })();

  /**
   * Muestra el botón de focus solo si hay más de un envío Y no todos tienen el mismo status.
   */
  const showFocusButton = (shipmentsCount > 1 || totalShipmentsNumber > 1) && !allShipmentsHaveSameStatus;

  const isCancelled = data?.statusName?.toLowerCase().trim() === ORDER_STATUS_NAME_TRIM.CANCELLED;
  const isDelivered = data?.statusName?.toLowerCase().trim() === ORDER_STATUS_NAME_TRIM.DELIVERED;

  /**
   * Determina si la orden está en estado de alerta (WARNING_24H u OVERDUE).
   * Se usa para aplicar estilos de tarjeta en rojo.
   * No aplica si la orden está cancelada o entregada.
   */
  const isDelayed =
    !isCancelled &&
    !isDelivered &&
    (data?.slaStatus === SLA_STATUS.WARNING_24H || data?.slaStatus === SLA_STATUS.OVERDUE);

  /**
   * Determina si la orden está próxima a atrasarse (WARNING_48H).
   * Se usa para aplicar estilos de tarjeta en amarillo.
   * No aplica si la orden está cancelada o entregada.
   */
  const isAboutToDelay = !isCancelled && !isDelivered && data?.slaStatus === SLA_STATUS.WARNING_48H;

  /**
   * Determina el color del icono de enfoque según el estado.
   */
  const focusIconColor = isFocused ? nudosBlue : bordColorTheme.primary.fgDefault;

  // Label y fecha según el estado de la orden
  const statusLabel = isCancelled ? 'Cancelado' : 'SLA';
  const statusDate = isCancelled ? formattedCancelingDate : slaDate;

  // Información para órdenes canceladas
  const paymentMethod = data?.paymentType || 'Sin información';
  const formattedAmount = `$${formatRoundedUpNumber(Number(data?.totalUsd || data?.total || 0), 2)} ${
    data?.currency || 'USD'
  }`;
  const responsibleUser = users.find(user => data?.responsibleIds?.includes(user.id));
  const responsibleName = responsibleUser
    ? `${responsibleUser.firstName || ''} ${responsibleUser.lastName || ''}`.trim() || 'Sin nombre'
    : 'Sin asignar';

  // Lógica de visibilidad de shipments
  const hasMoreShipments = shipmentsCount > MAX_VISIBLE_SHIPMENTS;
  const hasTooManyShipments = shipmentsCount > MAX_EXPANDABLE_SHIPMENTS;
  const visibleShipments = isShipmentsExpanded ? allShipmentsData : allShipmentsData.slice(0, MAX_VISIBLE_SHIPMENTS);

  const hasDrawerOpen = isShipmentDrawerOpen || isPreparationDrawerOpen;

  const cardClassNames = [
    'operationsCard',
    isAboutToDelay && 'aboutToDelay',
    (isDelayed || isCancelled) && 'delayed',
    isFocused && 'focused',
    hasDrawerOpen && 'drawerOpen'
  ]
    .filter(Boolean)
    .join(' ');

  const orderDetailUrl = data?.orderId ? `/order/details/${data.orderId}` : '#';

  const handleToggleShipments = () => setIsShipmentsExpanded(prev => !prev);
  const handleFocusOrder = () => setIsFocused(prev => !prev);
  const handleOpenOrCloseCommentsModal = () => setShowCommentsModal(!showCommentsModal);

  /**
   * Abre el drawer con el detalle del envío seleccionado.
   * @param shipmentId - ID del envío a mostrar
   * @param index - Índice del envío en la lista
   */
  const handleShipmentClick = (shipmentId: number, index: number) => {
    setSelectedShipmentId(shipmentId);
    setSelectedShipmentIndex(index);
    setIsShipmentDrawerOpen(true);
  };

  const handleCloseShipmentDrawer = () => {
    setIsShipmentDrawerOpen(false);
    setSelectedShipmentId(null);
  };

  /**
   * Abre el drawer de checklist de preparación para el envío seleccionado.
   * @param shipmentId - ID del envío a mostrar
   * @param index - Índice del envío en la lista
   */
  const handlePreparationClick = (shipmentId: number, index: number) => {
    setSelectedShipmentId(shipmentId);
    setSelectedShipmentIndex(index);
    setIsPreparationDrawerOpen(true);
  };

  const handleClosePreparationDrawer = () => {
    setIsPreparationDrawerOpen(false);
    setSelectedShipmentId(null);
  };

  return {
    // Estados
    isFocused,
    showCommentsModal,
    isShipmentsExpanded,
    isShipmentDrawerOpen,
    isPreparationDrawerOpen,
    selectedShipmentId,
    selectedShipmentIndex,

    // Valores derivados
    orderNumber,
    companyName,
    countryFlag,
    countryName,
    totalShipmentsNumber,
    slaDate,
    allShipmentsData,
    shipmentsCount,
    showFocusButton,
    isCancelled,
    isDelayed,
    isAboutToDelay,
    focusIconColor,
    statusLabel,
    statusDate,
    paymentMethod,
    formattedAmount,
    responsibleUser,
    responsibleName,
    hasMoreShipments,
    hasTooManyShipments,
    visibleShipments,
    cardClassNames,
    orderDetailUrl,

    // Handlers
    handleToggleShipments,
    handleFocusOrder,
    handleOpenOrCloseCommentsModal,
    handleShipmentClick,
    handleCloseShipmentDrawer,
    handlePreparationClick,
    handleClosePreparationDrawer
  };
};

export default useOperationsCard;
